import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus } from 'lucide-react';
import { isApiRequestError } from '../../lib/api/errors.js';
import { duplicatePhoneOf } from '../../lib/customers/customers-api.js';
import { useCreateCustomer, useCustomerSearch } from '../../lib/customers/use-customers.js';
import { formatMobile } from '../../lib/format.js';
import { Button, Input, Label } from '@tailoring-crm/ui';

/** The customer a form has settled on. The phone rides along for the card that shows the choice. */
export interface PickedCustomer {
  id: string;
  name: string;
  /** Empty when the pick came from somewhere that did not know the number. */
  phone: string;
}

/**
 * "Who is this for?", answered inside another form — find a customer, or add one on the spot.
 *
 * SEARCH FIRST, like every other "find the person" moment in the product: the counter knows the
 * name or the number it is looking for, and a shop with four hundred customers cannot be handed a
 * dropdown of them.
 *
 * QUICK-CREATE IS INLINE, NOT A SECOND DIALOG. This lives inside dialogs (the deal form), and a
 * dialog opened over a dialog fights it for the overlay and the focus trap. A name and a mobile
 * number are all a customer needs to exist; everything else is on their page afterwards. Whatever
 * was typed into the search seeds the new customer — digits become the number, anything else
 * the name — so the counter types the other half and nothing twice.
 *
 * A NUMBER THE SHOP ALREADY HAS IS NOT AN ERROR. The server answers 409 naming the customer who
 * holds it, and the picker offers that person instead — the counter nearly always meant them.
 */
export function CustomerPicker({
  value,
  onChange,
  disabled = false,
  label,
}: {
  value: PickedCustomer | null;
  onChange: (customer: PickedCustomer | null) => void;
  disabled?: boolean;
  /** Defaults to "Customer". */
  label?: string;
}): ReactElement {
  const { t } = useTranslation();
  const fieldId = useId();

  const [query, setQuery] = useState('');
  const trimmed = query.trim();
  const search = useCustomerSearch(
    { q: trimmed, pageSize: 6 },
    { enabled: !value && trimmed.length > 0 },
  );
  const matches = trimmed ? (search.data?.items ?? []) : [];

  const create = useCreateCustomer();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<PickedCustomer | null>(null);

  const startAdding = (): void => {
    const digits = trimmed.replace(/[\s-]/g, '');
    const isNumber = /^\+?\d+$/.test(digits);
    setNewName(isNumber ? '' : trimmed);
    setNewPhone(isNumber ? digits : '');
    setError(null);
    setExisting(null);
    setAdding(true);
  };

  const pick = (customer: PickedCustomer): void => {
    setAdding(false);
    setQuery('');
    setExisting(null);
    setError(null);
    onChange(customer);
  };

  const saveNew = (): void => {
    setError(null);
    setExisting(null);
    if (!newName.trim()) {
      setError(t('customerPicker.nameRequired'));
      return;
    }
    if (!newPhone.trim()) {
      setError(t('customerPicker.phoneRequired'));
      return;
    }
    create.mutate(
      { name: newName.trim(), primaryPhone: newPhone.trim() },
      {
        onSuccess: (customer) =>
          pick({ id: customer.id, name: customer.name, phone: customer.primaryPhone }),
        onError: (failure) => {
          const duplicate = duplicatePhoneOf(failure);
          if (duplicate) {
            setExisting({
              id: duplicate.existingCustomerId,
              name: duplicate.existingCustomerName,
              phone: newPhone.trim(),
            });
            return;
          }
          setError(isApiRequestError(failure) ? failure.message : t('error.unexpected'));
        },
      },
    );
  };

  if (value) {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium">{label ?? t('customerPicker.label')}</span>
        <div className="flex items-center justify-between gap-2 rounded-md border border-input px-3 py-2">
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{value.name}</span>
            {value.phone ? (
              <span className="block text-xs text-muted-foreground">
                {formatMobile(value.phone)}
              </span>
            ) : null}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            {t('customerPicker.change')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`${fieldId}-search`}>{label ?? t('customerPicker.label')}</Label>
      <Input
        id={`${fieldId}-search`}
        type="search"
        value={query}
        disabled={disabled}
        onChange={(event) => {
          setQuery(event.target.value);
          setAdding(false);
        }}
        placeholder={t('customerPicker.searchPlaceholder')}
        autoComplete="off"
      />

      {trimmed && !adding ? (
        <ul className="flex flex-col gap-1" aria-label={t('customerPicker.matches')}>
          {matches.map((customer) => (
            <li key={customer.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() =>
                  pick({ id: customer.id, name: customer.name, phone: customer.primaryPhone })
                }
                className="flex w-full items-baseline justify-between gap-2 rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="truncate font-medium">{customer.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatMobile(customer.primaryPhone)}
                </span>
              </button>
            </li>
          ))}
          {search.isFetching && matches.length === 0 ? null : (
            <li>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={startAdding}
              >
                <UserPlus aria-hidden="true" />
                {t('customerPicker.addNew', { query: trimmed })}
              </Button>
            </li>
          )}
        </ul>
      ) : null}

      {adding ? (
        <div className="flex flex-col gap-2 rounded-md border border-border p-3">
          <p className="text-sm font-medium">{t('customerPicker.newHeading')}</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-new-name`}>{t('customerPicker.nameLabel')}</Label>
            <Input
              id={`${fieldId}-new-name`}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-new-phone`}>{t('customerPicker.phoneLabel')}</Label>
            <Input
              id={`${fieldId}-new-phone`}
              type="tel"
              inputMode="tel"
              value={newPhone}
              onChange={(event) => setNewPhone(event.target.value)}
            />
          </div>

          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}

          {existing ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>{t('customerPicker.alreadyHave', { name: existing.name })}</span>
              <Button type="button" size="sm" variant="outline" onClick={() => pick(existing)}>
                {t('customerPicker.useExisting', { name: existing.name })}
              </Button>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={create.isPending}
              onClick={() => setAdding(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="button" size="sm" disabled={create.isPending} onClick={saveNew}>
              {t('customerPicker.save')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CustomerPicker;
